import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowRight, CheckCircle2, Stamp, Printer, Sparkles, AlertTriangle, FileSignature, Eye } from 'lucide-react';
import Header from '../components/Header';
import BulkActions from '../components/BulkActions';
import DatePickerField from '../components/DatePickerField';
import { ConsentDocV2, HealthDeclarationsCard } from '../components/ConsentDocV2';
import ConsentComplicationsPanel from '../components/ConsentComplicationsPanel';
import { MedicationsUsedPanel, MedicationsUsedPrint } from '../components/MedicationsUsedPanel';
import { SECTIONS } from '../lib/sectionTheme';
import { useAuth } from '../context/AuthContext';
import { parseMedicare, formatDigitsDisplay, formatMedicare } from '../lib/medicare';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = SECTIONS.consent;

// Preview/print styles shared between the live preview and the printed PDF.
// The content is identical so 'what you see is what you print'.
const PRINT_STYLES = `
  .consent-doc {
    max-width: 820px; margin: 0 auto;
    background: white; color: #111; font-family: 'Museo Sans','Nunito Sans','Inter',sans-serif;
    padding: 36px 44px; border: 1px solid #e5e7eb; border-radius: 8px;
  }
  .consent-doc .legal-header { border-bottom: 3px solid #0d2644; padding-bottom: 14px; margin-bottom: 22px; display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; }
  .consent-doc .legal-header .brand-left { display: flex; align-items: flex-start; gap: 14px; }
  .consent-doc .legal-header .brand-left img { width: 54px; height: 54px; object-fit: contain; flex: none; }
  .consent-doc .legal-title { font-family: 'Museo Slab','Zilla Slab','Outfit',serif; font-size: 22px; font-weight: 400; letter-spacing: -0.01em; line-height: 1.2; color: #0d2644; }
  .consent-doc .legal-meta { text-align: right; font-size: 10px; color: #6b7280; font-variant-numeric: tabular-nums; }
  .consent-doc h2 { font-family: 'Museo Slab','Zilla Slab','Outfit',serif; font-size: 13px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: #0d2644; border-bottom: 2px solid #52c3c5; padding-bottom: 4px; margin-top: 22px; margin-bottom: 10px; }
  .consent-doc p, .consent-doc li { font-size: 12.5px; line-height: 1.55; color: #111; }
  .consent-doc ul { padding-left: 20px; margin: 8px 0; }
  .consent-doc li { margin-bottom: 3px; list-style: disc; }
  .consent-doc .declaration { border: 2px solid #0d2644; border-radius: 6px; padding: 14px 18px; margin-top: 20px; background: #e8fafb; }
  .consent-doc .declaration ul { list-style: none; padding-left: 0; }
  .consent-doc .declaration li { position: relative; padding-left: 22px; margin-bottom: 6px; list-style: none; }
  .consent-doc .declaration li:before { content: '☐'; position: absolute; left: 0; top: -1px; font-size: 14px; color: #0d2644; }
  .consent-doc .declaration li.checked:before { content: '☑'; color: #52c3c5; }
  .consent-doc .sig-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 28px; padding-top: 18px; border-top: 1px solid #e5e7eb; }
  .consent-doc .sig-box { border-bottom: 1.5px solid #0d2644; padding-bottom: 6px; min-height: 36px; font-family: 'Caveat', cursive; font-size: 26px; color: #0d2644; }
  .consent-doc .sig-label { font-size: 9px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #6b7280; margin-top: 4px; }
  .consent-doc .small { font-size: 10px; color: #6b7280; }
  .consent-doc .pt-field { border-bottom: 1px solid #9ca3af; padding: 2px 4px; min-height: 20px; font-size: 13px; }
  .consent-doc .pt-row { display: grid; grid-template-columns: 160px 1fr; gap: 8px; align-items: end; margin-bottom: 8px; }
  .consent-doc .pt-row .pt-label { font-size: 11px; font-weight: 600; color: #374151; }
  .consent-doc .declare-line { font-weight: 600; color: #0d2644; font-size: 13.5px; margin-top: 12px; padding-top: 10px; border-top: 1px dashed #9ca3af; }
  .consent-doc .footer { margin-top: 24px; padding-top: 10px; border-top: 1px solid #e5e7eb; display: flex; justify-content: space-between; font-size: 9px; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.08em; }
  .consent-doc .brand-rule { height: 4px; background: linear-gradient(90deg, #0d2644 0%, #52c3c5 100%); margin-bottom: 18px; border-radius: 2px; }
  .consent-doc .warning-box { background: #FEF2F2; border: 1px solid #FCA5A5; border-radius: 6px; padding: 10px 14px; margin: 8px 0; }
  /* Two-column packing for patient details + health declarations on screen
     when there's space (≥720px). Print enforces it unconditionally below. */
  .consent-doc .pt-cols { display: grid; grid-template-columns: 1fr 1fr; column-gap: 28px; row-gap: 0; }
  @media (max-width: 720px) {
    .consent-doc .pt-cols { display: block; }
  }
  @media print {
    body { background: white !important; }
    [data-testid="brand-footer"] { display: none !important; }
    .consent-doc {
      box-shadow: none !important; border: none !important;
      padding: 0 !important; max-width: none !important;
      /* Iter 105 — body font kept at 12px (readable), but vertical spacing
         tightened so the document fits cleanly on 2 A4 pages instead of
         spilling onto a third. */
      font-size: 12px;
    }
    .consent-doc p, .consent-doc li { font-size: 12px !important; line-height: 1.4 !important; margin: 0 0 3px 0 !important; }
    .consent-doc ul { margin: 4px 0 !important; padding-left: 16px !important; }
    .consent-doc li { margin-bottom: 2px !important; }
    .consent-doc h2 { font-size: 12.5px !important; margin-top: 10px !important; margin-bottom: 5px !important; padding-bottom: 3px !important; }
    .consent-doc .legal-header { padding-bottom: 8px !important; margin-bottom: 9px !important; }
    .consent-doc .legal-header .brand-left img { width: 40px !important; height: 40px !important; }
    .consent-doc .legal-title { font-size: 17px !important; line-height: 1.15 !important; }
    .consent-doc .pt-row { grid-template-columns: 105px 1fr !important; margin-bottom: 3px !important; }
    .consent-doc .pt-row .pt-label { font-size: 10.5px !important; }
    .consent-doc .pt-field { font-size: 11.5px !important; min-height: 15px !important; padding: 1px 4px !important; }
    .consent-doc .pt-cols { display: grid !important; grid-template-columns: 1fr 1fr !important; column-gap: 20px !important; row-gap: 1px !important; }
    .consent-doc .declaration { padding: 7px 12px !important; margin-top: 8px !important; }
    .consent-doc .declaration li { padding-left: 18px !important; margin-bottom: 3px !important; }
    .consent-doc .declaration li:before { font-size: 12px !important; }
    .consent-doc .declare-line { font-size: 12px !important; margin-top: 7px !important; padding-top: 5px !important; }
    .consent-doc .sig-grid { gap: 16px !important; margin-top: 10px !important; padding-top: 7px !important; }
    .consent-doc .sig-box { font-size: 20px !important; min-height: 24px !important; padding-bottom: 3px !important; }
    .consent-doc .sig-label { font-size: 8.5px !important; }
    .consent-doc .footer { margin-top: 8px !important; padding-top: 5px !important; font-size: 8px !important; }
    .consent-doc .brand-rule { display: none !important; }
    .consent-doc .legal-header .small,
    .consent-doc .legal-meta { font-size: 9px !important; }
    .print-hidden { display: none !important; }
    .consent-form-grid { display: block !important; grid-template-columns: 1fr !important; }
    /* Avoid awkward page-break inside critical blocks */
    .consent-doc h2 { page-break-after: avoid; }
    .consent-doc .declaration, .consent-doc .sig-grid { page-break-inside: avoid; }
    @page { size: A4; margin: 8mm 9mm 8mm 9mm; }
  }
`;

// ────────────────────────────────────────────────────────────────────────────
// Index — tile grid of 8 consent forms + recent submissions link for PMs
// ────────────────────────────────────────────────────────────────────────────
export function ConsentIndexPage() {
  const { user } = useAuth();
  const isPM = user?.role === 'admin' || user?.role === 'superuser';
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/consent-templates`, { withCredentials: true })
      .then(r => setTemplates(r.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="consent-index">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header — title left, actions right (ReferralBase pattern) */}
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3" style={{ backgroundColor: `${C.color}1A`, color: C.color }}>
              <Sparkles className="w-3.5 h-3.5" />
              <span className="text-[10px] font-bold uppercase tracking-wider">Image-Guided Procedure Consent</span>
            </div>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl font-bold tracking-tight text-foreground">
              Consent <span style={{ color: C.color }}>forms</span>
            </h1>
            <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl">
              Australian <strong>Montgomery-standard</strong> consent forms. Each form walks the patient through the procedure, risks, alternatives, and a procedure-specific declaration. Complete on the tablet, print, or export as PDF.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <BulkActions resource="consent_templates" title="Consent templates" color={C.color} />
            <Link to="/screening" data-testid="consent-to-screening"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-xl border"
              style={{ borderColor: `${C.color}40`, color: C.color }}>
              <FileSignature className="w-4 h-4" /> Screening forms
            </Link>
            {isPM && (
              <Link to="/management" data-testid="consent-admin-link"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-xl"
                style={{ backgroundColor: C.color }}>
                <Eye className="w-4 h-4" /> Submissions dashboard
              </Link>
            )}
          </div>
        </div>

        {/* Always-visible KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="consent-stats">
          <ConsentKpi label="Templates" value={templates.length} color={C.color} />
          <ConsentKpi label="CT-guided" value={templates.filter(t => t.requires_claustrophobia_check).length} color="#2563EB" />
          <ConsentKpi label="Steroid (driver)" value={templates.filter(t => t.uses_steroid).length} color="#D97706" />
          <ConsentKpi label="Driver required" value={templates.filter(t => t.requires_driver).length} color="#7C3AED" />
        </div>

        {loading ? (
          <p className="p-6 text-center text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="consent-templates-list">
            {templates.map(t => (
              <Link
                key={t.template_id}
                to={`/consent/${t.template_id}`}
                data-testid={`consent-template-${t.template_id}`}
                className="group p-5 bg-card border border-border rounded-2xl hover:border-[--c] hover:shadow-sm transition-all"
                style={{ '--c': C.color, borderLeftWidth: 3, borderLeftColor: C.color }}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: C.color }}>{t.procedure_type}</p>
                    <h3 className="font-['Outfit'] text-base font-bold text-foreground mt-1">{t.title}</h3>
                    <p className="text-[11px] text-muted-foreground mt-1">v{t.version}
                      {t.requires_driver && <span> · Driver required</span>}
                      {t.uses_steroid && <span> · Steroid</span>}
                      {t.requires_claustrophobia_check && <span> · CT</span>}
                    </p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-1 transition-transform shrink-0" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ConsentKpi({ label, value, color }) {
  return (
    <div className="bg-card border border-border rounded-xl p-3">
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold mt-1" style={{ color }}>{value}</p>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Fill-in / live-preview page. Left column = inputs. Right column = the
// document exactly as it will print. Updates in real time.
// ────────────────────────────────────────────────────────────────────────────
export function ConsentFormPage() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [practice, setPractice] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null);

  const [patient, setPatient] = useState({
    name: '', prefix: '', first_name: '', middle_name: '', last_name: '',
    dob: '', gender: '', indigenous_status: '',
    address: '', address_line_1: '', address_line_2: '', suburb: '', state: 'NSW', postcode: '', country: 'Australia',
    phone: '', email: '', mobile: '', medicare: '',
  });
  const [biopsySite, setBiopsySite] = useState('');
  const [ticks, setTicks] = useState({});
  const [decl, setDecl] = useState({});
  const [signature, setSignature] = useState('');
  const [radName, setRadName] = useState('');

  const { user } = useAuth();
  const isPM = user?.role === 'admin' || user?.role === 'superuser';

  useEffect(() => {
    axios.get(`${API}/consent-templates/${templateId}`, { withCredentials: true })
      .then(r => setTemplate(r.data))
      .catch(() => setErr('Template not found'))
      .finally(() => setLoading(false));
    // Practice identifiers — stamped on the printed document header
    axios.get(`${API}/practice-settings`, { withCredentials: true })
      .then(r => setPractice(r.data || {}))
      .catch(() => {});
  }, [templateId]);

  if (loading) return <div className="min-h-screen bg-background"><Header /><p className="p-10 text-center text-sm text-muted-foreground">Loading…</p></div>;
  if (!template) return <div className="min-h-screen bg-background"><Header /><p className="p-10 text-center text-sm text-muted-foreground">{err}</p></div>;

  // Auto-derive canonical name + address from structured fields when present.
  const derivedName = patient.name?.trim() ||
    [patient.prefix, patient.first_name, patient.middle_name, patient.last_name].filter(Boolean).join(' ').trim();
  const derivedAddress = patient.address?.trim() ||
    [patient.address_line_1, patient.address_line_2, patient.suburb,
     [patient.state, patient.postcode].filter(Boolean).join(' '),
     patient.country].filter(Boolean).join(', ');

  const allTicked = (template.consent_statement || []).every(c => ticks[c.id]);
  const sitePicker = template.site_picker || null;
  const selectedSite = sitePicker
    ? (sitePicker.options || []).find(o => o.key === biopsySite)
    : null;
  const canSubmit = derivedName && signature.trim() && allTicked
    && (!sitePicker || !!selectedSite);

  const fillTestData = () => {
    setPatient({
      ...patient, prefix: 'Mr', first_name: 'Jonathan', middle_name: 'David', last_name: 'Pemberton',
      dob: '14/03/1972', mobile: '0412 345 678',
    });
    setDecl({
      btStatus: 'none', allergies: 'Penicillin (rash)', diabetic: false,
      medications: 'Atorvastatin 20 mg nocte\nParacetamol 1 g PRN', surgeries: 'Appendicectomy (1998)',
      covid2w: false, covidNext: false,
    });
    if (sitePicker?.options?.length) setBiopsySite(sitePicker.options[0].key);
    const allTicks = {};
    (template.consent_statement || []).forEach(c => { allTicks[c.id] = true; });
    setTicks(allTicks);
    setSignature('Jonathan David Pemberton');
  };

  const submit = async () => {
    setErr(''); setSubmitting(true);
    try {
      const r = await axios.post(`${API}/consents`, {
        template_id: templateId,
        patient: { ...patient, name: derivedName, address: derivedAddress },
        biopsy_site: biopsySite,
        blood_thinner_status: decl.btStatus || '',
        blood_thinner_ceased_date: decl.btCeased || '',
        allergies: decl.allergies || '',
        diabetic: decl.diabetic ?? null,
        medications: decl.medications || '',
        previous_surgeries: decl.surgeries || '',
        covid_vaccine_last_2_weeks: decl.covid2w ?? null,
        covid_vaccine_next_week: decl.covidNext ?? null,
        consent_ticks: ticks,
        universal_confirmation: allTicked,
        typed_signature: signature,
        radiologist_name: radName,
      }, { withCredentials: true });
      setDone(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Submission failed');
    }
    setSubmitting(false);
  };

  if (done) {
    return (
      <div className="min-h-screen bg-background" data-testid="consent-submitted">
        <Header />
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-6" style={{ backgroundColor: `${C.color}15` }}>
            <CheckCircle2 className="w-10 h-10" style={{ color: C.color }} />
          </div>
          <h1 className="font-['Outfit'] text-3xl font-bold text-foreground">Consent recorded.</h1>
          <p className="mt-2 text-sm text-muted-foreground">A time-stamped copy has been saved. Print for the patient file or hand them a PDF.</p>
          <div className="mt-8 flex items-center justify-center gap-2 flex-wrap">
            <Link to={`/consent/submission/${done.submission_id}`} data-testid="consent-done-view"
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg text-white"
              style={{ backgroundColor: C.color }}>
              <Printer className="w-4 h-4" /> View & print PDF
            </Link>
            <Link to="/consent" data-testid="consent-done-back"
              className="text-sm font-semibold px-4 py-2 rounded-lg border border-border">Back to consent library</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="consent-form-page">
      <div className="print-hidden"><Header /></div>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="print-hidden flex items-center justify-between gap-3 mb-4 flex-wrap">
          <button onClick={() => navigate('/consent')} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-4 h-4" /> Back to consent library
          </button>
          <div className="flex items-center gap-2">
            <Link to={`/consent/${templateId}/post-procedure-care`} target="_blank"
              data-testid="consent-pil-link"
              title="Open the one-page Post-Procedure Care information sheet for this procedure — to hand or email to the patient."
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg border"
              style={{ borderColor: `${C.color}55`, color: C.color }}>
              <FileSignature className="w-4 h-4" /> Post-procedure care PIL
            </Link>
            <button onClick={() => window.print()} data-testid="consent-print-capture"
              title="Print the form on the right. Leave the left blank to print a paper-fill copy for the patient, or fill on screen first and print the completed PDF for RIS."
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg border"
              style={{ borderColor: `${C.color}40`, color: C.color }}>
              <Printer className="w-4 h-4" /> Print / Save as PDF
            </button>
          </div>
        </div>

        <div className="print-hidden rounded-xl border border-dashed p-3 mb-4 text-[11px] text-muted-foreground flex items-start gap-2"
          style={{ borderColor: `${C.color}40`, backgroundColor: `${C.color}06` }}
          data-testid="consent-print-hint">
          <Printer className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: C.color }} />
          <span>
            <strong style={{ color: C.color }}>Need a paper copy?</strong> Click <strong>Print / Save as PDF</strong> above — it prints only the document on the right. Leave the fields empty for a paper-fill copy the patient can complete by hand, or fill on screen first to print the completed form for scanning into the RIS.
          </span>
        </div>

        <div className="consent-form-grid grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
          {/* Left: Inputs */}
          <div className="space-y-4 print-hidden" data-testid="consent-inputs">
            {isPM && (
              <button onClick={fillTestData} type="button" data-testid="c-fill-test-data"
                className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-dashed"
                style={{ borderColor: `${C.color}60`, color: C.color, backgroundColor: `${C.color}06` }}
                title="Admin only — populate every field with realistic dummy data so you can stress-test the live preview / print layout">
                <Sparkles className="w-3.5 h-3.5" /> Fill test data (admin)
              </button>
            )}

            <div className="bg-card border border-border rounded-2xl p-4" data-testid="c-patient-card">
              <p className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: C.color }}>Patient details</p>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Field label="First name *" value={patient.first_name} onChange={v => setPatient({ ...patient, first_name: v })} testid="c-patient-first" />
                <Field label="Last name *" value={patient.last_name} onChange={v => setPatient({ ...patient, last_name: v })} testid="c-patient-last" />
                <label className="block col-span-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Date of birth</span>
                  <DatePickerField value={patient.dob} onChange={v => setPatient({ ...patient, dob: v })}
                    format="au" testid="c-patient-dob" yearRangeStart={1910} yearRangeEnd={(new Date()).getFullYear()} />
                </label>
              </div>
              <div className="mt-2">
                <Field label="Phone" value={patient.mobile} onChange={v => setPatient({ ...patient, mobile: v })} testid="c-patient-mobile" placeholder="0412 345 678" />
              </div>
            </div>

            <HealthDeclarationsCard template={template} decl={decl} setDecl={setDecl} />

            {sitePicker && (
              <div className="bg-card border border-border rounded-2xl p-4" data-testid="c-site-picker">
                <p className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: C.color }}>Biopsy site</p>
                <p className="text-[11px] text-muted-foreground mt-1">{sitePicker.label}</p>
                <div className="space-y-2 mt-2">
                  {(sitePicker.options || []).map(o => (
                    <RadioRow key={o.key} checked={biopsySite === o.key} onChange={() => setBiopsySite(o.key)}
                      testid={`c-site-${o.key}`} label={o.label} />
                  ))}
                </div>
              </div>
            )}

            <div className="bg-card border border-border rounded-2xl p-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: C.color }}>Consent declaration — tick every box</p>
              <ul className="space-y-2 mt-2" data-testid="c-declaration">
                {(template.consent_statement || []).map(c => (
                  <li key={c.id}>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input type="checkbox" checked={!!ticks[c.id]}
                        onChange={e => setTicks({ ...ticks, [c.id]: e.target.checked })}
                        data-testid={`c-tick-${c.id}`}
                        className="w-4 h-4 mt-0.5 shrink-0 rounded border-border" />
                      <span className="text-xs text-foreground">{c.text}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-card border border-border rounded-2xl p-4 space-y-3">
              <Field label="Patient signature (typed full name) *" value={signature} onChange={setSignature} testid="c-signature" />
              <Field label="Witnessing radiologist (optional at capture)" value={radName} onChange={setRadName} testid="c-radiologist" />
            </div>

            {err && <p className="text-xs text-red-500" data-testid="c-error">{err}</p>}

            <div className="flex items-center justify-end gap-2 sticky bottom-4 pt-2">
              <button onClick={submit} disabled={!canSubmit || submitting}
                data-testid="c-submit"
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-5 py-3 rounded-xl text-white disabled:opacity-40 shadow-lg"
                style={{ backgroundColor: C.color }}>
                <CheckCircle2 className="w-4 h-4" /> {submitting ? 'Saving…' : 'Record consent'}
              </button>
            </div>
          </div>

          {/* Right: Live document preview */}
          <div className="overflow-auto" data-testid="consent-preview-pane">
            <style>{PRINT_STYLES}</style>
            <ConsentDocV2
              template={template}
              practice={practice}
              patient={{ ...patient, name: derivedName }}
              biopsySite={selectedSite}
              ticks={ticks} decl={decl}
              signature={signature} radName={radName}
              signedAt={null}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// The actual legal document layout, used both for live preview and print.
// ────────────────────────────────────────────────────────────────────────────
function LegacyConsentDoc({ template, practice = {}, patient, btStatus, btCeased, allergies, medications,
                     driverBrought, covid2w, covidNext, claustro, biopsySite, safetyScreen,
                     ticks, universal, signature, radName,
                     signedAt, countersignedAt, countersignedBy }) {
  const issued = signedAt ? new Date(signedAt).toLocaleString('en-AU') : 'Draft — live preview';
  // Iter 134 — site picker substitution for the printed document, so the
  // patient only sees the site-specific risks relevant to their procedure.
  const sitePicker = template.site_picker || null;
  const renderBulletText = (c) => {
    if (sitePicker && biopsySite && c.id === sitePicker.substitute_bullet_id) {
      return biopsySite.risk_text;
    }
    return c.text;
  };
  // Build the practice address line from structured fields, fall back to the
  // legacy single-line `address` when structured fields are empty.
  const addrLine = practice.address_line_1 || practice.address_line_2 || practice.suburb
    ? [practice.address_line_1, practice.address_line_2, practice.suburb,
       [practice.state, practice.postcode].filter(Boolean).join(' '),
       practice.country].filter(Boolean).join(', ')
    : (practice.address || '');
  const idTags = [
    practice.abn && `ABN ${practice.abn}`,
    practice.lspn && `LSPN ${practice.lspn}`,
    practice.hpi_o && `HPI-O ${practice.hpi_o}`,
  ].filter(Boolean).join(' · ');
  const contactLine = [
    practice.phone && `Ph ${practice.phone}`,
    practice.fax && `Fax ${practice.fax}`,
    practice.email,
  ].filter(Boolean).join(' · ');

  return (
    <div className="consent-doc" data-testid="consent-doc">
      <div className="brand-rule" />
      {/* Header */}
      <div className="legal-header">
        <div className="brand-left">
          <img src="/brand/i3-mark.png" alt="" aria-hidden />
          <div>
            <p className="small" style={{ color: '#0d2644', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase' }}>
              {practice.practice_name || 'i3 Radiology'} · Infinity · Imaging · Interventions
            </p>
            <h1 className="legal-title">{template.title}</h1>
            <p className="small" style={{ marginTop: 4, fontStyle: 'italic' }}>Image-Guided Procedure · Consent &amp; Patient Declaration</p>
            {(addrLine || idTags || contactLine) && (
              <p className="small" style={{ marginTop: 3, color: '#475569', lineHeight: 1.4 }}>
                {addrLine && <>{addrLine}<br /></>}
                {idTags && <>{idTags}{contactLine && <><br /></>}</>}
                {contactLine}
              </p>
            )}
          </div>
        </div>
        <div className="legal-meta">
          <div>Version {template.version}</div>
          <div>{issued}</div>
        </div>
      </div>

      {/* Introduction */}
      <h2>1 · The procedure</h2>
      <p><strong>Your doctor has requested an image-guided procedure: {template.procedure_type}.</strong></p>
      <p style={{ whiteSpace: 'pre-wrap', marginTop: 6 }}>{template.procedure_description}</p>

      <h2>2 · Purpose</h2>
      <ul>
        {(template.purpose || []).map((p, i) => <li key={i}>{p}</li>)}
      </ul>

      {template.biopsy_post && <p style={{ marginTop: 8 }}>{template.biopsy_post}</p>}

      {/* Iter 115 — Post-procedure care moved to a separate one-page Patient
          Information Leaflet (PIL) to keep the consent form to two A4 pages.
          A short acknowledgment remains here to preserve the legal record
          that the patient received and read those instructions. */}
      <p className="pil-ack" style={{ marginTop: 10, padding: '6px 10px', border: '1px solid #cbd5e1',
                                       borderLeft: `3px solid ${C.color}`, background: '#f8fafc',
                                       fontSize: 11.5, borderRadius: 4 }}>
        <strong>Post-procedure care:</strong> I confirm I have received and read the separate
        <em> {template.procedure_type} — Post-Procedure Care Information Sheet</em>, which
        details what to expect after the procedure and the warning signs that should prompt
        me to contact this practice or my GP.
      </p>

      {/* Patient details */}
      <h2>3 · Patient details</h2>
      <div className="pt-cols">
        <div className="pt-row"><span className="pt-label">Full name</span><span className="pt-field">{patient.name || '\u00A0'}</span></div>
        <div className="pt-row"><span className="pt-label">Date of birth</span><span className="pt-field">{patient.dob || '\u00A0'}</span></div>
        <div className="pt-row"><span className="pt-label">Sex</span><span className="pt-field">{patient.gender || '\u00A0'}</span></div>
        <div className="pt-row"><span className="pt-label">ATSI status</span><span className="pt-field">{patient.indigenous_status || '\u00A0'}</span></div>
        <div className="pt-row"><span className="pt-label">Address</span><span className="pt-field">{patient.address || '\u00A0'}</span></div>
        <div className="pt-row"><span className="pt-label">Mobile</span><span className="pt-field">{patient.mobile || patient.phone || '\u00A0'}</span></div>
        <div className="pt-row"><span className="pt-label">Email</span><span className="pt-field">{patient.email || '\u00A0'}</span></div>
        <div className="pt-row"><span className="pt-label">Medicare</span><span className="pt-field">{patient.medicare || '\u00A0'}</span></div>
      </div>

      {/* Health declarations */}
      <h2>4 · Health declarations</h2>
      <ul className="declaration-list">
        <li>Blood thinning medication: <strong>
          {btStatus === 'none' && 'I do not take any blood thinning medication.'}
          {btStatus === 'ceased' && `Ceased${btCeased ? ` on ${btCeased}` : ''}.`}
          {btStatus === 'still_taking' && 'I am still taking my blood thinning medication.'}
        </strong></li>
      </ul>
      <div className="pt-cols">
        <div className="pt-row"><span className="pt-label">Allergies</span><span className="pt-field">{allergies || '\u00A0'}</span></div>
        <div className="pt-row"><span className="pt-label">Medications</span><span className="pt-field">{medications || '\u00A0'}</span></div>
        {template.requires_driver && (
          <div className="pt-row"><span className="pt-label">Driver arranged</span><span className="pt-field">
            {driverBrought === true ? 'Yes — driver present' : driverBrought === false ? 'No driver' : '\u00A0'}
          </span></div>
        )}
        {template.requires_claustrophobia_check && (
          <div className="pt-row"><span className="pt-label">Claustrophobic</span><span className="pt-field">
            {claustro === true ? 'Yes' : claustro === false ? 'No' : '\u00A0'}
          </span></div>
        )}
        {sitePicker && (
          <div className="pt-row"><span className="pt-label">{sitePicker.title || 'Biopsy site'}</span><span className="pt-field">
            {biopsySite?.label || '\u00A0'}
          </span></div>
        )}
        {template.uses_steroid && (
          <>
            <div className="pt-row"><span className="pt-label">COVID vaccine (last 2 weeks)</span><span className="pt-field">
              {covid2w === true ? 'Yes' : covid2w === false ? 'No' : '\u00A0'}
            </span></div>
            <div className="pt-row"><span className="pt-label">COVID vaccine (next week)</span><span className="pt-field">
              {covidNext === true ? 'Yes' : covidNext === false ? 'No' : '\u00A0'}
            </span></div>
          </>
        )}
      </div>
      {template.uses_steroid && (
        <p style={{ marginTop: 4, fontSize: 10, fontStyle: 'italic', color: '#374151' }}>{template.covid_blurb}</p>
      )}

      {/* Iter 82 — Pre-procedure safety screen (RANZCR/CIRSE/InsideRadiology). */}
      <SafetyScreenPrinted template={template} screen={safetyScreen} btStatus={btStatus} patientGender={patient.gender} />

      {/* Consent declaration */}
      <h2>5 · Consent declaration</h2>
      <p style={{ marginBottom: 8 }}>I confirm that:</p>
      <div className="declaration">
        <ul>
          {(template.consent_statement || []).map(c => (
            <li key={c.id} className={ticks[c.id] ? 'checked' : ''}>{renderBulletText(c)}</li>
          ))}
        </ul>
        <p className="declare-line">{template.voluntary_line}</p>
        <p style={{ marginTop: 10, fontWeight: 600, fontSize: 12.5 }}>
          <span style={{ marginRight: 6, color: C.color }}>{universal ? '☑' : '☐'}</span>
          {template.universal_confirmation}
        </p>
      </div>

      {/* Signature */}
      <div className="sig-grid">
        <div>
          <div className="sig-box">{signature || '\u00A0'}</div>
          <div className="sig-label">Patient signature</div>
          <div className="small" style={{ marginTop: 2 }}>{signedAt ? new Date(signedAt).toLocaleString('en-AU') : 'Date: __________________'}</div>
        </div>
        <div>
          <div className="sig-box">{(countersignedBy?.name || radName) || '\u00A0'}</div>
          <div className="sig-label">Radiologist / clinician (witness)</div>
          <div className="small" style={{ marginTop: 2 }}>{countersignedAt ? new Date(countersignedAt).toLocaleString('en-AU') : 'Date: __________________'}</div>
        </div>
      </div>

      <div className="footer">
        <span>i3 Radiology · Image-Guided Procedure Consent</span>
        <span>{template.title} · v{template.version}</span>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Submission detail — used for print / PDF export of a completed consent
// ────────────────────────────────────────────────────────────────────────────
export function ConsentSubmissionPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPM = user?.role === 'admin' || user?.role === 'superuser';
  const [data, setData] = useState(null);
  const [practice, setPractice] = useState({});
  const [err, setErr] = useState('');
  const [radName, setRadName] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/consents/${id}`, { withCredentials: true });
      setData(r.data);
      setRadName(r.data.submission.radiologist_name || r.data.submission.countersigned_by?.name || '');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Not found');
    }
  };
  useEffect(() => {
    load();
    axios.get(`${API}/practice-settings`, { withCredentials: true })
      .then(r => setPractice(r.data || {}))
      .catch(() => {});
    /* eslint-disable-next-line */
  }, [id]);

  if (err) return <div className="min-h-screen bg-background"><Header /><p className="p-10 text-center text-sm text-muted-foreground">{err}</p></div>;
  if (!data) return <div className="min-h-screen bg-background"><Header /><p className="p-10 text-center text-sm text-muted-foreground">Loading…</p></div>;

  const s = data.submission;
  const t = data.template;

  const print = () => window.print();
  const countersign = async () => {
    if (!radName.trim()) { alert('Enter radiologist name first.'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/consents/${id}/countersign`, { radiologist_name: radName.trim(), note }, { withCredentials: true });
      await load();
    } catch (e) { alert(e?.response?.data?.detail || 'Countersign failed'); }
    setBusy(false);
  };
  const onDelete = async () => {
    if (!window.confirm('Delete this signed consent permanently?')) return;
    try {
      await axios.delete(`${API}/consents/${id}`, { withCredentials: true });
      navigate('/consent');
    } catch (e) { alert('Failed'); }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="consent-submission-page">
      <div className="print-hidden"><Header /></div>

      <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-5 print-hidden">
          <button onClick={() => navigate('/consent')} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-center gap-2">
            <button onClick={print} data-testid="consent-print"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-xl"
              style={{ backgroundColor: C.color }}>
              <Printer className="w-4 h-4" /> Print / Save PDF
            </button>
          </div>
        </div>

        {/* Admin countersign panel (above the doc, not printed) */}
        {isPM && s.status !== 'countersigned' && (
          <div className="print-hidden mb-5 p-4 bg-card border border-border rounded-2xl" data-testid="consent-countersign-panel">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: C.color }}>Countersign</p>
            <p className="text-xs text-muted-foreground mt-0.5">Add the witnessing radiologist / clinician name and a note for the file.</p>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 mt-2">
              <input type="text" value={radName} onChange={e => setRadName(e.target.value)}
                placeholder="Radiologist name"
                data-testid="consent-countersign-name"
                className="text-sm bg-background border border-border rounded-lg px-3 py-2" />
              <input type="text" value={note} onChange={e => setNote(e.target.value)}
                placeholder="Note (optional)"
                data-testid="consent-countersign-note"
                className="text-sm bg-background border border-border rounded-lg px-3 py-2" />
              <button onClick={countersign} disabled={busy}
                data-testid="consent-countersign-save"
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-40"
                style={{ backgroundColor: C.color }}>
                <Stamp className="w-4 h-4" /> Countersign
              </button>
            </div>
          </div>
        )}

        {/* Iter 84 — Post-procedure complications audit log (Phase 3).
            Visible to all staff who can view the submission; PMs can close
            outcomes; admins can delete. Logged events flow into the DIAS
            evidence pack as `14_consent_complications.csv`. */}
        <ConsentComplicationsPanel submissionId={id} color={C.color} />
        <MedicationsUsedPanel submission={s} color={C.color} onSaved={load} />

        {/* Status banner */}
        <div className="print-hidden mb-4 text-xs flex items-center gap-2">
          {s.status === 'countersigned' ? (
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full font-semibold"
              style={{ backgroundColor: '#16A34A15', color: '#16A34A' }}>
              <CheckCircle2 className="w-3 h-3" /> Countersigned {s.countersigned_at ? `on ${new Date(s.countersigned_at).toLocaleString('en-AU')}` : ''}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full font-semibold"
              style={{ backgroundColor: '#F59E0B15', color: '#F59E0B' }}>
              <AlertTriangle className="w-3 h-3" /> Awaiting clinician countersign
            </span>
          )}
          {isPM && (
            <button onClick={onDelete}
              data-testid="consent-delete"
              className="ml-auto text-[10px] font-semibold px-2 py-1 rounded-md border border-border text-red-500 hover:bg-red-500/10">
              Delete
            </button>
          )}
        </div>

        <style>{PRINT_STYLES}</style>
        {s.template_wording_unavailable && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-xs p-3 mb-3" data-testid="consent-legacy-wording-banner">
            Signed on version {s.template_version}. The original wording of that version was not archived — the text below
            is the current version ({t.version}) for reference only. Ticks, signature and patient answers are as recorded.
          </div>
        )}
        {t?.safety_checklist ? (
          <ConsentDocV2
            template={t}
            practice={practice}
            patient={s.patient}
            biopsySite={(t?.site_picker?.options || []).find(o => o.key === s.biopsy_site) || null}
            ticks={s.consent_ticks}
            decl={{
              btStatus: s.blood_thinner_status, btCeased: s.blood_thinner_ceased_date, allergies: s.allergies,
              diabetic: s.diabetic, medications: s.medications, surgeries: s.previous_surgeries,
              covid2w: s.covid_vaccine_last_2_weeks, covidNext: s.covid_vaccine_next_week,
            }}
            signature={s.typed_signature}
            radName={s.radiologist_name}
            signedAt={s.submitted_at}
            countersignedAt={s.countersigned_at}
            countersignedBy={s.countersigned_by}
          />
        ) : (
        <LegacyConsentDoc
          template={t}
          practice={practice}
          patient={s.patient}
          btStatus={s.blood_thinner_status}
          btCeased={s.blood_thinner_ceased_date}
          allergies={s.allergies}
          medications={s.medications}
          driverBrought={s.driver_brought}
          covid2w={s.covid_vaccine_last_2_weeks}
          covidNext={s.covid_vaccine_next_week}
          claustro={s.claustrophobic}
          biopsySite={(t?.site_picker?.options || []).find(o => o.key === s.biopsy_site) || null}
          safetyScreen={s.safety_screen}
          ticks={s.consent_ticks}
          universal={s.universal_confirmation}
          signature={s.typed_signature}
          radName={s.radiologist_name}
          signedAt={s.submitted_at}
          countersignedAt={s.countersigned_at}
          countersignedBy={s.countersigned_by}
        />
        )}
        <MedicationsUsedPrint submission={s} />
      </div>
    </div>
  );
}

// ─────────────────────────── UI helpers ───────────────────────────
// Pre-procedure safety screen — printed/preview block (Iter 82).
// Shows only the answered fields so blank screens don't pad the PDF.
function SafetyScreenPrinted({ template, screen, btStatus, patientGender }) {
  const s = screen || {};
  const yn = (v) => v === true ? 'Yes' : v === false ? 'No' : '—';
  const list = (arr) => (arr && arr.length) ? arr.join(', ') : '—';

  const infectionPos = s.current_infection === true || s.recent_antibiotics === true
    || s.septic_arthritis_history === true || s.prosthetic_joint_at_site === true
    || (s.immunosuppression_flags || []).length > 0;

  const timeoutAll = ['timeout_id_confirmed', 'timeout_site_correct', 'timeout_side_correct',
    'timeout_procedure_correct', 'timeout_medication_correct', 'timeout_imaging_reviewed']
    .every(k => s[k]);

  const sterilePrepLabel = {
    'chlorhex_2_alcohol': '2% Chlorhexidine in 70% alcohol',
    'betadine': 'Betadine / povidone-iodine',
    'other': s.timeout_sterile_prep_other || 'Other',
  }[s.timeout_sterile_prep] || '—';

  return (
    <>
      <h2>4b · Pre-procedure safety screen</h2>
      {/* Compact two-column key-value grid mirrors the Health declarations style. */}
      <div className="pt-cols">
        <div className="pt-row"><span className="pt-label">Current infection</span>
          <span className="pt-field">{yn(s.current_infection)}{(s.current_infection_sites || []).length ? ` (${list(s.current_infection_sites)})` : ''}</span></div>
        <div className="pt-row"><span className="pt-label">Infection 2–4w</span>
          <span className="pt-field">{yn(s.recent_infection_2_4w)}</span></div>
        <div className="pt-row"><span className="pt-label">Antibiotics 1–2w</span>
          <span className="pt-field">{yn(s.recent_antibiotics)}{s.recent_antibiotics_reason ? ` — ${s.recent_antibiotics_reason}` : ''}</span></div>
        <div className="pt-row"><span className="pt-label">Septic arthritis hx</span>
          <span className="pt-field">{yn(s.septic_arthritis_history)}</span></div>
        <div className="pt-row"><span className="pt-label">Prosthetic joint</span>
          <span className="pt-field">{yn(s.prosthetic_joint_at_site)}</span></div>
        <div className="pt-row"><span className="pt-label">Immunosuppression</span>
          <span className="pt-field">{list(s.immunosuppression_flags)}</span></div>
        <div className="pt-row"><span className="pt-label">Skin at site clear</span>
          <span className="pt-field">{yn(s.skin_check_clear)}{s.skin_findings ? ` — ${s.skin_findings}` : ''}</span></div>
        <div className="pt-row"><span className="pt-label">Anticoag agents</span>
          <span className="pt-field">{list(s.anticoag_agents)}{s.anticoag_inr ? ` · INR ${s.anticoag_inr}` : ''}</span></div>
        {(btStatus === 'still_taking' || (s.anticoag_agents || []).length > 0) && (
          <div className="pt-row"><span className="pt-label">Anticoag decision</span>
            <span className="pt-field">{s.anticoag_decision === 'continue' ? 'Continue (peripheral)' : s.anticoag_decision === 'withhold' ? 'Withhold (spinal/deep)' : '—'}</span></div>
        )}
        {template.uses_steroid && (
          <>
            <div className="pt-row"><span className="pt-label">Diabetic</span>
              <span className="pt-field">{yn(s.diabetic)}{s.diabetes_type ? ` (${s.diabetes_type})` : ''}{s.hba1c ? ` · HbA1c ${s.hba1c}` : ''}</span></div>
            <div className="pt-row"><span className="pt-label">Insulin</span>
              <span className="pt-field">{yn(s.insulin_use)}</span></div>
          </>
        )}
        <div className="pt-row"><span className="pt-label">Allergy flags</span>
          <span className="pt-field">{list(s.allergy_flags)}</span></div>
        <div className="pt-row"><span className="pt-label">Prior reaction</span>
          <span className="pt-field">{s.prior_injection_reaction || 'None'}</span></div>
        {template.uses_steroid && (
          <>
            <div className="pt-row"><span className="pt-label">Vaccine 2w prior</span>
              <span className="pt-field">{yn(s.vaccine_recent_2w)}{s.vaccine_recent_2w_type ? ` — ${s.vaccine_recent_2w_type}` : ''}</span></div>
            <div className="pt-row"><span className="pt-label">Vaccine planned</span>
              <span className="pt-field">{yn(s.vaccine_planned_2w)}{s.vaccine_planned_2w_type ? ` — ${s.vaccine_planned_2w_type}` : ''}</span></div>
          </>
        )}
        <div className="pt-row"><span className="pt-label">Pregnant</span>
          <span className="pt-field">{patientGender === 'Male' ? 'N/A' : yn(s.pregnant)}</span></div>
        <div className="pt-row"><span className="pt-label">Possibly pregnant</span>
          <span className="pt-field">{patientGender === 'Male' ? 'N/A' : yn(s.possibly_pregnant)}</span></div>
        <div className="pt-row"><span className="pt-label">Breastfeeding</span>
          <span className="pt-field">{patientGender === 'Male' ? 'N/A' : yn(s.breastfeeding)}</span></div>
        {template.uses_steroid && (
          <>
            <div className="pt-row"><span className="pt-label">Prior injection (region)</span>
              <span className="pt-field">{yn(s.has_prior_injection)}{s.last_injection_date ? ` · ${s.last_injection_date}` : ''}{s.last_injection_response ? ` · ${s.last_injection_response}` : ''}</span></div>
            <div className="pt-row"><span className="pt-label">Injections / 12 mo</span>
              <span className="pt-field">{s.injections_last_12m ?? '—'}</span></div>
          </>
        )}
      </div>
      {infectionPos && (
        <p className="warning-box" style={{ marginTop: 6 }}>
          <strong style={{ color: '#7F1D1D' }}>Caution:</strong> at least one infection-related risk factor declared.
          Document clinical justification before proceeding.
        </p>
      )}

      {/* Iter 83 — higher-risk template extras printed inline. */}
      {template.risk_tier === 'higher' && (
        <>
          <p style={{ marginTop: 6 }}><strong>Higher-risk procedure — additional checks:</strong></p>
          <div className="pt-cols">
            <div className="pt-row"><span className="pt-label">No contraindications</span>
              <span className="pt-field">{s.no_exclusions_present ? 'Confirmed' : '— NOT CONFIRMED'}</span></div>
            <div className="pt-row"><span className="pt-label">Neuro baseline intact</span>
              <span className="pt-field">{yn(s.neuro_baseline_intact)}{s.neuro_baseline_notes ? ` — ${s.neuro_baseline_notes}` : ''}</span></div>
            <div className="pt-row"><span className="pt-label">Pain VAS (pre)</span>
              <span className="pt-field">{(s.pain_vas_pre ?? '—')} / 10</span></div>
            {(s.anticoag_agents || []).length > 0 && (
              <div className="pt-row"><span className="pt-label">Cessation matrix</span>
                <span className="pt-field">{s.anticoag_cessation_confirmed ? 'Confirmed (per matrix)' : '— NOT CONFIRMED'}</span></div>
            )}
          </div>
          {(!s.no_exclusions_present || s.neuro_baseline_intact === null) && (
            <p className="warning-box" style={{ marginTop: 6 }}>
              <strong style={{ color: '#7F1D1D' }}>Do not proceed:</strong> exclusion flowchart or neuro baseline incomplete.
            </p>
          )}
        </>
      )}

      {/* Clinician time-out — printed regardless so the file always carries it. */}
      <h2>4c · Clinician time-out (WHO-style)</h2>
      <div className="declaration" style={{ marginTop: 4 }}>
        <ul>
          <li className={s.timeout_id_confirmed       ? 'checked' : ''}>Patient identity verified (name + DOB)</li>
          <li className={s.timeout_site_correct       ? 'checked' : ''}>Correct site</li>
          <li className={s.timeout_side_correct       ? 'checked' : ''}>Correct side (L / R)</li>
          <li className={s.timeout_procedure_correct  ? 'checked' : ''}>Correct procedure</li>
          <li className={s.timeout_medication_correct ? 'checked' : ''}>Correct medication & dose</li>
          <li className={s.timeout_imaging_reviewed   ? 'checked' : ''}>Imaging reviewed by performing radiologist</li>
        </ul>
        <div className="pt-row" style={{ marginTop: 6 }}>
          <span className="pt-label">Skin prep</span>
          <span className="pt-field">{sterilePrepLabel}</span>
        </div>
        {!timeoutAll && (
          <p style={{ marginTop: 4, fontSize: 11, color: '#7F1D1D' }}>
            <em>Time-out incomplete — must be ticked before the needle is inserted.</em>
          </p>
        )}
      </div>
    </>
  );
}

function Field({ label, value, onChange, testid, placeholder }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      <input type="text" value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
    </label>
  );
}


// Australian Medicare card — 10 digits displayed as "XXXX XXXXX X", plus the
// 1-digit IRN (Individual Reference Number, 1–9) for the family-member line.
// Stored as "XXXX XXXXX X / N" so downstream code / prints see it formatted.
export function MedicareField({ value, onChange, testid }) {
  const parsed = parseMedicare(value);
  const [digits, setDigits] = useState(parsed.digits);
  const [irn, setIrn] = useState(parsed.irn);

  useEffect(() => {
    // Keep internal state in sync if parent resets the field (e.g. form reset).
    const p = parseMedicare(value);
    if (p.digits !== digits) setDigits(p.digits);
    if (p.irn !== irn) setIrn(p.irn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = (d, i) => {
    const formatted = d.length === 0 && i.length === 0 ? '' : formatMedicare(d, i);
    onChange(formatted);
  };
  const onDigitsChange = (raw) => {
    const d = raw.replace(/\D/g, '').slice(0, 10);
    setDigits(d);
    emit(d, irn);
  };
  const onIrnChange = (raw) => {
    const i = raw.replace(/[^1-9]/g, '').slice(0, 1);
    setIrn(i);
    emit(digits, i);
  };

  const complete = digits.length === 10 && irn.length === 1;
  const partial = digits.length > 0 || irn.length > 0;
  const invalid = partial && !complete;

  return (
    <div>
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Medicare</span>
      <div className="mt-1 grid grid-cols-[1fr_72px] gap-2">
        <input type="text" inputMode="numeric" autoComplete="off"
          value={formatDigitsDisplay(digits)}
          onChange={e => onDigitsChange(e.target.value)}
          placeholder="XXXX XXXXX X"
          maxLength={12}
          aria-label="Medicare card number"
          data-testid={testid}
          className="w-full text-sm bg-background border rounded-lg px-3 py-2 font-mono tracking-wider"
          style={{ borderColor: invalid ? '#DC2626' : 'hsl(var(--border))' }} />
        <input type="text" inputMode="numeric" autoComplete="off"
          value={irn}
          onChange={e => onIrnChange(e.target.value)}
          placeholder="IRN"
          maxLength={1}
          aria-label="Medicare IRN (reference number, 1–9)"
          data-testid={testid ? `${testid}-irn` : undefined}
          className="w-full text-sm bg-background border rounded-lg px-3 py-2 font-mono text-center"
          style={{ borderColor: invalid ? '#DC2626' : 'hsl(var(--border))' }} />
      </div>
      <p className="text-[10px] text-muted-foreground mt-1">
        10-digit card number + 1-digit IRN (family-line number, 1–9).
        {invalid && <span className="ml-1 font-semibold" style={{ color: '#DC2626' }}>Enter all 10 digits and the IRN.</span>}
      </p>
    </div>
  );
}
function RadioRow({ checked, onChange, testid, label, warn }) {
  return (
    <label className="flex items-start gap-2 cursor-pointer">
      <input type="radio" checked={checked} onChange={onChange}
        data-testid={testid}
        className="w-4 h-4 mt-0.5" />
      <span className="text-xs" style={{ color: warn && checked ? '#DC2626' : 'hsl(var(--foreground))', fontWeight: warn && checked ? 600 : 400 }}>{label}</span>
    </label>
  );
}


// ────────────────────────────────────────────────────────────────────────────
// Post-Procedure Care PIL (Patient Information Leaflet) — iter 115
// Standalone one-A4-page handout that's given to the patient AFTER the
// procedure. Extracted out of the main consent form (which previously
// overflowed to 3 pages when section 3 was inline). Kept deliberately
// short and visually clear so the patient can see the warning signs at
// a glance.
// ────────────────────────────────────────────────────────────────────────────
export function PostProcedureCareLeafletPage() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [practice, setPractice] = useState({});
  const [err, setErr] = useState('');

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/consent-templates/${templateId}`, { withCredentials: true }).then(r => r.data),
      axios.get(`${API}/practice-settings`, { withCredentials: true }).then(r => r.data).catch(() => ({})),
    ]).then(([t, p]) => { setTemplate(t); setPractice(p); })
      .catch(e => setErr(e?.response?.data?.detail || 'Could not load template'));
  }, [templateId]);

  if (err) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-2xl mx-auto p-8 text-center">
          <AlertTriangle className="w-10 h-10 mx-auto text-amber-500" />
          <p className="mt-3 text-base font-semibold">{err}</p>
          <button onClick={() => navigate('/consent')}
            className="mt-4 text-sm font-semibold px-4 py-2 rounded-lg border border-border">Back to consent library</button>
        </div>
      </div>
    );
  }
  if (!template) return null;

  const issued = template.issued_at ? new Date(template.issued_at).toLocaleDateString('en-AU') : '';
  const addrLine = practice.address_line_1 || practice.address_line_2 || practice.suburb
    ? [practice.address_line_1, practice.address_line_2, practice.suburb,
       [practice.state, practice.postcode].filter(Boolean).join(' ')].filter(Boolean).join(', ')
    : (practice.address || '');
  const contactLine = [
    practice.phone && `Ph ${practice.phone}`,
    practice.fax && `Fax ${practice.fax}`,
    practice.email,
  ].filter(Boolean).join('  ·  ');

  return (
    <div className="min-h-screen bg-background" data-testid="pil-page">
      <style>{PIL_STYLES}</style>
      <div className="print-hidden"><Header /></div>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="print-hidden flex items-center justify-between gap-3 mb-4 flex-wrap">
          <button onClick={() => navigate(`/consent/${templateId}`)} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-4 h-4" /> Back to consent
          </button>
          <button onClick={() => window.print()} data-testid="pil-print"
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg border"
            style={{ borderColor: `${C.color}55`, color: C.color }}>
            <Printer className="w-4 h-4" /> Print / Save as PDF
          </button>
        </div>

        <article className="pil-doc" data-testid="pil-doc">
          <header className="pil-header">
            <div className="pil-brand">
              <img src="/brand/i3-mark.png" alt="" aria-hidden />
              <div>
                <p className="pil-practice">{practice.practice_name || 'i3 Radiology'} · Infinity · Imaging · Interventions</p>
                <h1 className="pil-title">Post-procedure care</h1>
                <p className="pil-sub">{template.procedure_type} · Patient information leaflet</p>
              </div>
            </div>
            <div className="pil-meta">
              <div>Issued {issued}</div>
              <div>v{template.version}</div>
            </div>
          </header>

          <section className="pil-section">
            <h2>What to expect</h2>
            <p>
              You have just had an <strong>{template.procedure_type}</strong>. Mild discomfort,
              bruising or a small amount of bleeding at the injection site is common and usually
              settles within 24–48 hours. Most patients can return to light activity the same day.
            </p>
          </section>

          <section className="pil-section pil-warning">
            <h2>When to seek help</h2>
            <p className="pil-note">{template.infection_note}</p>
            <ul>
              {(template.infection_signs || []).map((s, i) => <li key={i}>{s}</li>)}
            </ul>
            <p className="pil-callout"><strong>The symptoms above may indicate infection</strong> and need to be treated as soon as possible.</p>
          </section>

          <section className="pil-section pil-contact">
            <h2>Who to contact</h2>
            <ol>
              <li><strong>During practice hours</strong> — phone the practice on the number below and ask to speak to a radiology nurse or the on-call radiologist.</li>
              <li><strong>After hours</strong> — contact your GP, or in an emergency call <strong>000</strong> or attend your nearest emergency department.</li>
            </ol>
          </section>

          <footer className="pil-footer">
            <div>
              <p className="pil-practice-name">{practice.practice_name || 'i3 Radiology'}</p>
              {addrLine && <p>{addrLine}</p>}
              {contactLine && <p>{contactLine}</p>}
            </div>
            <div className="pil-disclaimer">
              This leaflet is provided as a companion to the signed consent form for
              {' '}<em>{template.title}</em>. If you have any concerns that aren&apos;t covered
              above, please contact us — we&apos;d rather hear from you and reassure you than
              have you wait and worry.
            </div>
          </footer>
        </article>
      </div>
    </div>
  );
}

// Styles for the PIL — completely independent of the main consent form
// PRINT_STYLES so we can tune the typography for a single-A4-page
// patient handout (slightly larger body text, more breathing room).
const PIL_STYLES = `
  .pil-doc {
    background: white; color: #111;
    font-family: 'Outfit', 'Museo Sans', 'Nunito Sans', 'Inter', sans-serif;
    padding: 40px 48px; border: 1px solid #e5e7eb; border-radius: 8px;
    max-width: 820px; margin: 0 auto;
  }
  .pil-header {
    display: flex; justify-content: space-between; align-items: flex-start; gap: 16px;
    border-bottom: 3px solid #0d2644; padding-bottom: 16px; margin-bottom: 24px;
  }
  .pil-brand { display: flex; align-items: flex-start; gap: 14px; }
  .pil-brand img { width: 58px; height: 58px; object-fit: contain; flex: none; }
  .pil-practice {
    color: #0d2644; font-weight: 700; font-size: 10px;
    letter-spacing: 0.18em; text-transform: uppercase; margin: 0;
  }
  .pil-title {
    font-family: 'Zilla Slab','Museo Slab','Outfit',serif;
    font-size: 26px; font-weight: 600; letter-spacing: -0.01em;
    line-height: 1.15; color: #0d2644; margin: 4px 0 2px;
  }
  .pil-sub { font-size: 12px; color: #475569; font-style: italic; margin: 0; }
  .pil-meta { text-align: right; font-size: 10.5px; color: #6b7280; font-variant-numeric: tabular-nums; }

  .pil-section { margin-top: 18px; }
  .pil-section h2 {
    font-family: 'Zilla Slab','Museo Slab','Outfit',serif;
    font-size: 13px; font-weight: 600; letter-spacing: 0.1em;
    text-transform: uppercase; color: #0d2644;
    border-bottom: 2px solid #52c3c5; padding-bottom: 4px;
    margin: 0 0 10px;
  }
  .pil-section p, .pil-section li { font-size: 13.5px; line-height: 1.6; color: #111; }
  .pil-section ul, .pil-section ol { padding-left: 22px; margin: 6px 0; }
  .pil-section li { margin-bottom: 4px; }

  .pil-warning {
    background: #fef2f2; border: 1.5px solid #fecaca; border-radius: 6px;
    padding: 14px 18px;
  }
  .pil-warning h2 { color: #b91c1c; border-bottom-color: #fca5a5; }
  .pil-warning .pil-note { font-weight: 500; }
  .pil-warning .pil-callout {
    margin-top: 10px; padding: 8px 12px; background: #fff; border-left: 3px solid #b91c1c;
    font-size: 12.5px; font-style: italic; border-radius: 3px;
  }

  .pil-contact { background: #f0fdfa; border: 1.5px solid #99f6e4; border-radius: 6px; padding: 14px 18px; }
  .pil-contact h2 { color: #0d9488; border-bottom-color: #5eead4; }
  .pil-contact ol li::marker { font-weight: 700; color: #0d9488; }

  .pil-footer {
    margin-top: 26px; padding-top: 14px; border-top: 1px solid #e5e7eb;
    display: grid; grid-template-columns: 1fr 1.2fr; gap: 24px; font-size: 11px; color: #475569;
  }
  .pil-footer .pil-practice-name { color: #0d2644; font-weight: 700; font-size: 11.5px; margin: 0 0 2px; }
  .pil-footer p { margin: 0; line-height: 1.5; }
  .pil-disclaimer { font-style: italic; line-height: 1.5; }

  @media print {
    @page { size: A4; margin: 12mm 14mm; }
    html, body { background: white !important; }
    body * { visibility: hidden !important; }
    .pil-doc, .pil-doc * { visibility: visible !important; }
    .pil-doc {
      position: absolute; left: 0; top: 0; width: 100%;
      max-width: none; margin: 0; padding: 0; border: 0;
    }
    .print-hidden { display: none !important; }
  }
`;
